import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useHistory } from 'react-router';
import { TwoStepVerificationError } from '../../../../common/request/types/twoStepVerification';
import InlineChallengeBody from '../../../common/inlineChallengeBody';
import {
  mapTwoStepVerificationErrorToChallengeErrorCode,
  mapTwoStepVerificationErrorToResource
} from '../constants/resources';
import { mediaTypeToPath } from '../hooks/useActiveMediaType';
import useTwoStepVerificationContext from '../hooks/useTwoStepVerificationContext';
import { MediaType } from '../interface';
import { TwoStepVerificationActionType } from '../store/action';

type Props = {
  hasSentEmailCode: boolean;
  setHasSentEmailCode: React.Dispatch<React.SetStateAction<boolean>>;
  requestInFlight: boolean;
  setRequestInFlight: React.Dispatch<React.SetStateAction<boolean>>;
  // eslint-disable-next-line react/require-default-props
  children?: React.ReactNode;
};

/**
 * A list of available media types to solve the 2SV challenge.
 */
const MediaTypeList: React.FC<Props> = ({
  hasSentEmailCode,
  setHasSentEmailCode,
  requestInFlight,
  setRequestInFlight,
  children
}: Props) => {
  const {
    state: {
      userId,
      challengeId,
      actionType,
      renderInline,
      resources,
      requestService,
      enabledMediaTypes
    },
    dispatch
  } = useTwoStepVerificationContext();
  const history = useHistory();

  /*
   * Component State
   */

  const [sendCodeError, setSendCodeError] = useState<string | null>(null);

  /*
   * Event Handlers
   */

  const transitionToMediaType = async (mediaType: MediaType) => {
    if (requestInFlight) {
      return;
    }

    // Automatically send a 2SV email when switching to email code type.
    if (mediaType === MediaType.Email && !hasSentEmailCode) {
      setRequestInFlight(true);

      const result = await requestService.twoStepVerification.sendEmailCode(userId, {
        challengeId,
        actionType
      });
      setRequestInFlight(false);
      if (result.isError) {
        if (
          result.error === TwoStepVerificationError.INVALID_USER_ID ||
          result.error === TwoStepVerificationError.INVALID_CHALLENGE_ID
        ) {
          dispatch({
            type: TwoStepVerificationActionType.SET_CHALLENGE_INVALIDATED,
            errorCode: mapTwoStepVerificationErrorToChallengeErrorCode(result.error)
          });
        } else {
          setSendCodeError(mapTwoStepVerificationErrorToResource(resources, result.error));
        }
        return;
      }

      setHasSentEmailCode(true);
    }

    history.push(mediaTypeToPath(mediaType));
  };

  /*
   * Rendering Helpers
   */

  const getMediaTypeIcon = (mediaType: MediaType): string => {
    switch (mediaType) {
      case MediaType.Authenticator:
        return 'icon-menu-mobile';
      case MediaType.Email:
        return 'icon-menu-email';
      case MediaType.RecoveryCode:
        return 'icon-menu-recover';
      default:
        return 'icon-brokenpage';
    }
  };

  const getMediaTypeLabel = (mediaType: MediaType): string | null => {
    switch (mediaType) {
      case MediaType.Authenticator:
        return resources.Label.AuthenticatorMediaType;
      case MediaType.Email:
        return resources.Label.EmailMediaType;
      case MediaType.RecoveryCode:
        return resources.Label.RecoveryCodeMediaType;
      default:
        return null;
    }
  };

  const renderMediaType = (mediaType: MediaType, key: number): JSX.Element | null => {
    const mediaTypeLabel = getMediaTypeLabel(mediaType);
    if (!mediaTypeLabel) {
      return null;
    }

    return (
      <tr
        key={key}
        onClick={requestInFlight ? undefined : () => transitionToMediaType(mediaType)}
        className={requestInFlight ? 'media-type-row disabled' : 'media-type-row'}>
        <td>
          <span className={getMediaTypeIcon(mediaType)} />
        </td>
        <td className='media-type-label'>{mediaTypeLabel}</td>
        <td className='media-type-selector'>
          <span className='icon-next' />
          <div className='icon-placeholder' />
        </td>
      </tr>
    );
  };

  /*
   * Render Properties
   */

  const BodyElement = renderInline ? InlineChallengeBody : Modal.Body;
  const lockIconClassName = renderInline ? 'inline-challenge-lock-icon' : 'modal-lock-icon';
  const marginBottomXLargeClassName = renderInline
    ? 'inline-challenge-margin-bottom-xlarge'
    : 'modal-margin-bottom-xlarge';
  const tableMarginClassName = renderInline ? '' : 'modal-margin-bottom-large';
  const errorTextMarginClassName = renderInline
    ? 'inline-challenge-margin-top-large'
    : 'modal-margin-bottom-large';

  /*
   * Component Markup
   */

  return (
    <BodyElement>
      <div className={lockIconClassName} />
      <p className={marginBottomXLargeClassName}>{resources.Label.ChooseAlternateMediaType}</p>
      <table className={`table table-striped media-type-list ${tableMarginClassName}`}>
        <tbody>
          {enabledMediaTypes.map((mediaType, index) => renderMediaType(mediaType, index))}
        </tbody>
      </table>
      {sendCodeError ? (
        <p className={`text-error xsmall ${errorTextMarginClassName}`}>{sendCodeError}</p>
      ) : null}
      {children}
    </BodyElement>
  );
};

export default MediaTypeList;
